import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SEO from '../../components/SEO';
import {
  Crown,
  Calendar,
  CheckCircle,
  ExternalLink,
  Mail,
  AlertCircle,
  Loader2,
  CalendarCheck,
  CreditCard
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

import {
  getUserPlanType,
  getPlanDisplayName
} from '../../utils/subscription';

export default function Subscription() {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        window.REACT_APP_NAVIGATE('/login');
        return;
      }

      setUser(user);
      await fetchSubscription(user.id);
    } catch (error) {
      console.error('Erreur:', error);
      window.REACT_APP_NAVIGATE('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching subscription:', error);
      }

      setSubscription(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleOpenPortal = async () => {
    try {
      setPortalLoading(true);

      // D'abord essayer getSession
      let { data: { session } } = await supabase.auth.getSession();

      // Si pas de session, essayer de rafraîchir
      if (!session?.access_token) {
        console.log('⚠️ No session found, trying to refresh...');
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          console.error('❌ Refresh failed:', refreshError);
          throw new Error(t('subscription.manage.session_expired'));
        }
        session = refreshData.session;
      }

      if (!session?.access_token) {
        throw new Error(t('subscription.manage.session_expired'));
      }

      console.log('🔑 Calling portal with token:', session.access_token.substring(0, 20) + '...');

      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Accept-Language': i18n.language,
        }
      });

      console.log('📦 Portal response:', { data, error });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else if (data?.error) {
        throw new Error(data.error);
      }
    } catch (error: any) {
      console.error('Error opening portal:', error);
      const errorMsg = t('subscription.manage.error', {
        message: error.message || t('subscription.manage.error_default')
      });
      alert(errorMsg);
    } finally {
      setPortalLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; labelKey: string }> = {
      active: { bg: 'bg-green-500/10 border-green-500/20', text: 'text-green-500', labelKey: 'subscription.card.status.active' },
      canceled: { bg: 'bg-orange-500/10 border-orange-500/20', text: 'text-orange-500', labelKey: 'subscription.card.status.canceled' },
      expired: { bg: 'bg-red-500/10 border-red-500/20', text: 'text-red-500', labelKey: 'subscription.card.status.expired' }
    };

    const badge = badges[status] || badges.expired;

    return (
      <span className={`px-3 py-1 ${badge.bg} border ${badge.text} text-sm font-bold rounded-full`}>
        {t(badge.labelKey)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const isActive = subscription && subscription.status === 'active';

  return (
    <DashboardLayout user={user}>
      <SEO noindex={true} />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-main mb-2 flex items-center gap-2">
            {t('subscription.title')} <Crown className="text-primary fill-primary" />
          </h1>
          <p className="text-text-muted">{t('subscription.subtitle')}</p>
        </div>

        {/* No Subscription */}
        {!subscription && (
          <div className="bg-dark-card rounded-2xl border border-dark-border p-12 text-center">
            <div className="w-24 h-24 bg-dark-bg rounded-full flex items-center justify-center mx-auto mb-6 border border-dark-border">
              <Crown className="text-text-muted" size={48} />
            </div>
            <h2 className="text-2xl font-bold text-text-main mb-4">
              {t('subscription.empty.title')}
            </h2>
            <p className="text-text-muted mb-8 max-w-md mx-auto">
              {t('subscription.empty.desc')}
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-colors shadow-lg hover:shadow-primary/20"
            >
              <Crown size={20} />
              {t('subscription.empty.cta')}
            </Link>
          </div>
        )}

        {/* Active Subscription */}
        {subscription && (
          <div className="space-y-6">
            {/* Subscription Card */}
            <div className="bg-dark-card rounded-2xl border border-dark-border overflow-hidden">
              <div className={`h-32 ${isActive ? 'bg-gradient-to-r from-primary/20 to-green-900/20' : 'bg-dark-bg'
                } flex items-center justify-center relative border-b border-dark-border`}>
                <Crown size={64} className={isActive ? 'text-primary' : 'text-text-muted'} />
              </div>

              <div className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-3xl font-bold text-text-main mb-2">
                      {getPlanDisplayName(subscription)}
                    </h2>
                    <p className="text-text-muted">
                      {subscription.plan_type === 'monthly' ? t('subscription.card.billing_monthly') : t('subscription.card.billing_yearly')}
                    </p>
                  </div>
                  {getStatusBadge(subscription.status)}
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-dark-bg border border-dark-border rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center border border-primary/20">
                        <Calendar className="text-primary" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-text-muted">{t('subscription.card.start_date')}</p>
                        <p className="font-semibold text-text-main">
                          {formatDate(subscription.current_period_start || subscription.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-dark-bg border border-dark-border rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center border border-primary/20">
                        <CalendarCheck className="text-primary" size={20} />
                      </div>
                      <div>
                        <p className="text-sm text-text-muted">
                          {isActive ? t('subscription.card.next_renewal') : t('subscription.card.end_date')}
                        </p>
                        <p className="font-semibold text-text-main">
                          {subscription.current_period_end
                            ? formatDate(subscription.current_period_end)
                            : subscription.end_date
                              ? formatDate(subscription.end_date)
                              : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Benefits */}
                <div className="bg-gradient-to-br from-primary/5 to-transparent border border-primary/10 rounded-xl p-6 mb-6">
                  <h3 className="text-lg font-bold text-text-main mb-4 flex items-center gap-2">
                    <CheckCircle className="text-primary" size={20} />
                    {t('subscription.benefits.title', { plan: getPlanDisplayName(subscription) })}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {(getUserPlanType(subscription) === 'premium'
                      ? (t('subscription.benefits.premium', { returnObjects: true }) as string[])
                      : (t('subscription.benefits.standard', { returnObjects: true }) as string[])
                    ).map((benefit, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-text-muted">
                        <CheckCircle className="text-primary" size={16} />
                        {benefit}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/dashboard"
                    className="flex-1 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-colors text-center"
                  >
                    {t('subscription.actions.continue')}
                  </Link>
                  <Link
                    to="/pricing"
                    className="flex-1 py-3 bg-dark-bg text-text-main font-bold rounded-xl border border-dark-border hover:bg-dark-hover transition-colors text-center"
                  >
                    {isActive ? t('subscription.actions.compare') : t('subscription.actions.renew')}
                  </Link>
                </div>
              </div>
            </div>

            {/* Manage Subscription Info */}
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-500/10 rounded-full flex-shrink-0 border border-blue-500/20">
                  <CreditCard className="text-blue-500" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-text-main mb-2">
                    {t('subscription.manage.title')}
                  </h3>
                  <p className="text-text-muted mb-4">
                    {t('subscription.manage.desc')}
                  </p>
                  <button
                    onClick={handleOpenPortal}
                    disabled={portalLoading}
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-dark-bg text-text-main font-semibold rounded-xl border border-dark-border hover:bg-dark-hover transition-colors text-sm disabled:opacity-50"
                  >
                    {portalLoading ? (
                      <Loader2 className="animate-spin" size={16} />
                    ) : (
                      <ExternalLink size={16} />
                    )}
                    {t('subscription.manage.button')}
                  </button>
                </div>
              </div>
            </div>

            {/* Support */}
            <div className="bg-dark-card rounded-2xl shadow-lg border border-dark-border p-6">
              <h3 className="text-lg font-bold text-text-main mb-2">
                {t('subscription.support.title')}
              </h3>
              <p className="text-text-muted mb-4">
                {t('subscription.support.desc')}
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="mailto:citoyenpro@nnt-quebec.com"
                  className="flex items-center gap-2 px-6 py-2.5 bg-dark-bg text-text-muted font-semibold rounded-xl border border-dark-border hover:text-primary hover:border-primary/50 transition-colors"
                >
                  <Mail size={16} />
                  citoyenpro@nnt-quebec.com
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
